import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
   DeleteFilled,
   EditFilled,
   PlusOutlined,
   SearchOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import {
   Row,
   Col,
   Table,
   Input,
   Button,
   Breadcrumb
} from "common/elements";
import {
   Pending, 
   Submitted 
} from "common/status";
import Enum from "common/enums";
import { Util } from "common/util";
import { 
   getQuarterlyReviews,
   deletePDDById
} from "services/pdd";

const pathName = "/performance";

const params = new URLSearchParams(document.location.search);
const size = params.get("size");
let defaultPageSize = 20;

if (size) {
   defaultPageSize = size;
}

export default function QuarterlyReview() {
   const [datas, setDatas] = React.useState([]);
   const [loading, setLoading] = React.useState(true);

   const navigate = useNavigate();

   const fetchList = () => {
      setLoading(true);
      getQuarterlyReviews()
      .then((response) => {
         setDatas(response);
      })
      .catch(error => {
         if (error.response.status === 403) {
            localStorage.removeItem("ACCESS_TOKEN");
            navigate("/signin");
         }
      })
      .finally(() => setLoading(false));
   };

   const onDelete = (id) => {
      Util.getInstance()
         .sweetAlertConfirm(t("text_confrim"), "You won't be able to revert this!", ["Cancel", "Yes, delete it!"])
         .then((willDelete) => {
            if (willDelete) {
               deletePDDById(id)
               .then(() => {
                  fetchList();
               });
            }
         });
   };

   const onTableChange = (newPagination, filters, sorter) => {
      fetchList({
         sortField: sorter.field,
         sortOrder: sorter.order,
         pagination: newPagination,
         ...filters,
      });
   };

   const onChangePagination = (page, pageSize) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", page);
      params.set("size", pageSize);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
   };

   const onShowSizeChange = (current, size) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", current);
      params.set("size", size);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
   };

   React.useEffect(() => {
      fetchList();
      // eslint-disable-next-line
   }, []);

   const pagination = {
      current: params.has("current") ? parseInt(params.get("current")) : 1,
      defaultPageSize,
      pageSizeOptions: ["20", "30", "50", "100"],
      total: datas.pagination?.total,
      showSizeChanger: true,
      locale: { items_per_page: "" },
      onChange: onChangePagination,
      onShowSizeChange,
      showTotal: (total, range) => `${t("text_display")} ${range[0]}-${range[1]} ${t("text_out_of")} ${total}`,
   };

   return (
      <>
         <Row style={styles.breadCream}>
            <Col span={24}>
               <Breadcrumb>
                  <Breadcrumb.Item
                     onClick={() => navigate("/")}
                     style={{ cursor: "pointer" }}
                  >
                     {t("text_dashboard")}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item style={{ color: "#1d8747" }}>
                     Quarterly Review
                  </Breadcrumb.Item>
               </Breadcrumb>
            </Col>
         </Row>

         <Row style={{ padding: 15 }}>
            <Col span={8}>
               <Title title="Quarterly Review" />
            </Col>
            <Col span={16} style={{ textAlign: "right", bottom: "5px" }}>
               <Input
                  placeholder={t("text_search")}
                  defaultValue={params.get("search")}
                  prefix={<SearchOutlined />}
                  style={styles.generalSearch}
                  allowClear={true}
               />
               <Link to="/performance/quarterly-review/create" className="ant-btn ant-btn-primary" style={{marginLeft: 15}}><PlusOutlined /> New Quarterly Review</Link>
            </Col>
         </Row>

         <div className="data-table" style={{ flex: 1, display: "block" }}>
            <Table
               dataSource={datas.data}
               pagination={pagination}
               onChange={onTableChange}
               rowKey={(record) => `${record.id}`}
               scroll={window.innerHeight - 310}
               columns={[
                  {
                     title: t("text_code"),
                     dataIndex: "employeeCode",
                     key: "employeeCode",
                     width: 120
                     },
                  {
                     title: t("text_employee_name"),
                     dataIndex: "employeeName",
                     key: "employeeName"
                  },
                  {
                     title: "Date of Quarterly Review",
                     dataIndex: "reviewedDate",
                     key: "reviewedDate",
                     render: (reviewedDate) => moment(reviewedDate).format("DD/MM/YYYY")
                  },
                  {
                     title: "Status",
                     dataIndex: "status",
                     key: "status",
                     render: (status) => {
                        if (Enum.OBJECTIVES_SETTING.PENDING === status) {
                           return <Pending />;
                        } else if (Enum.OBJECTIVES_SETTING.SUBMITTED === status) {
                           return <Submitted />;
                        }
                     }
                  },
                  {
                     title: t("text_action"),
                     dataIndex: "id",
                     key: "id",
                     width: 100,
                     render: (id) => {
                        return <div id="btn-action">
                           <Link 
                              to={`/performance/quarterly-review/edit/${id}`}
                              className="ant-btn ant-btn-primary ant-btn-icon-only"
                              title="Edit"
                              style={{
                                 marginLeft: 5,
                                 height: 35,
                                 width: 35,
                                 borderRadius: 35,
                                 border: "none"
                              }}
                           >
                              <EditFilled style={{ color: "#fff" }} />
                           </Link>
                           <Button
                              danger
                              title={t("text_delete")}
                              icon={<DeleteFilled style={{ color: "#fff" }} />}
                              shape="circle"
                              type="primary"
                              style={{
                                 marginLeft: 5,
                                 height: 35,
                                 width: 35,
                                 borderRadius: 35,
                                 border: "none"
                              }}
                              onClick={() => onDelete(id)}
                           />
                        </div>
                     }
                  },
               ]}
               loading={loading}
            />
         </div>
      </>
   );
}

function Title({ title }) {
   return <h4 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h4>;
}

const styles = {
   generalSearch: {
      width: 200,
   },
   headerFilter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
   },
   breadCream: {
      paddingBottom: 15,
      backgroundColor: "#EFF3F6",
   },
   style: {
      background: "#0092ff",
      padding: "8px 0",
   },
};
